import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { pdfjs } from 'react-pdf';
import PDFViewer from './PDFViewer';

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

function MediaViewer({ mediaUrl, fileName: propFileName }) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [mediaType, setMediaType] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    const fetchMediaType = async () => {
      const urlPath =
        typeof mediaUrl === 'string' ? mediaUrl.split('?')[0] : '';
      
      // Use propFileName if available, otherwise fallback to urlPath
      const targetPath = propFileName || urlPath;
      const fileExtension = targetPath.split('.').pop().toLowerCase();

      if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
        setMediaType(`image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`);
      } else if (['mp4', 'webm', 'ogg'].includes(fileExtension)) {
        setMediaType(`video/${fileExtension}`);
      } else if (fileExtension === 'pdf') {
        setMediaType('application/pdf');
      } else {
        setMediaType('files');
      }
    };

    fetchMediaType();
    setPdfError(null);
  }, [mediaUrl, propFileName]);

  useEffect(() => {
    // Disable right-click globally inside viewer
    const disableContext = (e) => e.preventDefault();
    window.addEventListener('contextmenu', disableContext);

    // Disable screenshot-related keys
    const disableKeys = (e) => {
      const key = e.key.toLowerCase();

      // Block PrintScreen
      if (key === 'printscreen') {
        e.preventDefault();
        navigator.clipboard.writeText(''); // Clear clipboard
        alert('Screenshots are disabled.');
      }

      // Block Ctrl+P (Print)
      if (e.ctrlKey && key === 'p') {
        e.preventDefault();
        alert('Printing is disabled.');
      }

      // Block Ctrl+S (Save As)
      if (e.ctrlKey && key === 's') {
        e.preventDefault();
        alert('Saving is disabled.');
      }

      // Block Ctrl+Shift+I (DevTools)
      if (e.ctrlKey && e.shiftKey && key === 'i') {
        e.preventDefault();
      }

      // Block Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && key === 'c') {
        e.preventDefault();
      }

      // Block Ctrl+Shift+J
      if (e.ctrlKey && e.shiftKey && key === 'j') {
        e.preventDefault();
      }
      // Block Print dialog for both Windows (Ctrl+P) and Mac (Cmd+P)
      if ((e.ctrlKey || e.metaKey) && key === 'p') {
        e.preventDefault();
        alert('Printing is disabled.');
      }

      // Block F12
      if (key === 'f12') {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', disableKeys);

    return () => {
      window.removeEventListener('contextmenu', disableContext);
      window.removeEventListener('keydown', disableKeys);
    };
  }, []);

  const fileName =
    typeof mediaUrl === 'string' ? mediaUrl.split('?')[0].split('/').pop() : '';
  const isImage = mediaType?.startsWith('image/');
  const isVideo = mediaType?.startsWith('video/');
  const isPdf = mediaType === 'application/pdf';

  const handleDownload = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const downloadName = decodeURI(fileName) || 'download';

    // Use XMLHttpRequest to completely bypass axios interceptors and timeout
    const xhr = new XMLHttpRequest();
    xhr.open('GET', mediaUrl, true);
    xhr.responseType = 'blob';
    // No timeout limit for downloads
    xhr.timeout = 0;

    xhr.onload = () => {
      if (xhr.status === 200) {
        const contentType = xhr.getResponseHeader('Content-Type');
        const blob = new Blob([xhr.response], { type: contentType || 'application/octet-stream' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = downloadName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 200);
      } else {
        console.error('Download failed with status:', xhr.status);
        toast.error('Failed to download file');
        window.open(mediaUrl, '_blank');
      }
    };

    xhr.onerror = () => {
      console.error('Download XHR error');
      toast.error('Failed to download file');
      window.open(mediaUrl, '_blank');
    };

    xhr.ontimeout = () => {
      console.error('Download timed out');
      toast.error('Failed to download file');
      window.open(mediaUrl, '_blank');
    };

    xhr.send();
  };

  return (
    <>
      <div className="w-full md:max-w-[330px] max-w-[220px]">
        {isImage && (
          <img
            src={mediaUrl}
            alt=""
            draggable="false"
            className="w-full min-h-[330px] aspect-[4/3] object-cover cursor-pointer rounded"
            onClick={() => setIsFullScreen(true)}
          />
        )}
        {isVideo && (
          <video
            src={mediaUrl}
            draggable="false"
            className="w-full aspect-[4/3] object-cover cursor-pointer rounded"
            onClick={() => setIsFullScreen(true)}
            controls
          />
        )}
        {isPdf && !isFullScreen && (
          <div
            className="cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors"
            onClick={() => setIsFullScreen(true)}
          >
            <div className="bg-gray-50 p-2 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M13 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V9M13 2L20 9M13 2V9H20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm font-medium truncate">
                  {decodeURI(fileName)}
                </span>
              </div>
            </div>
            <PDFViewer url={mediaUrl} onError={setPdfError} />
          </div>
        )}
        {!isImage && !isVideo && !isPdf && (
          <div className="md:w-[330px] w-[220px]">
            <button
              onClick={handleDownload}
              className="w-full text-left"
            >
              <div className="flex items-center justify-between p-3 bg-gray-100 border rounded-md">
                <div className="flex gap-2">
                  <div className="flex items-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M13 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V9M13 2L20 9M13 2V9H20"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="text-base font-medium">
                      <div className="overflow-hidden truncate overflow-ellipsis whitespace-nowrap md:w-[200px] w-[130px]">
                        {decodeURI(fileName)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-gray-400">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        )}
      </div>

      {isFullScreen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-100 ">
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b">
            <div className="flex items-center gap-3">
              {isPdf ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M13 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V9M13 2L20 9M13 2V9H20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : isImage ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.5 10C9.32843 10 10 9.32843 10 8.5C10 7.67157 9.32843 7 8.5 7C7.67157 7 7 7.67157 7 8.5C7 9.32843 7.67157 10 8.5 10Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 15L16 10L5 21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M10 8L16 12L10 16V8Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <h2 className="text-lg font-medium truncate">
                {decodeURI(fileName)}
              </h2>
            </div>
            <div className="flex items-center gap-2 mr-6">
              {/* <button
                onClick={handleDownload}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button> */}
              <button
                className="p-2 hover:bg-gray-100 rounded-full"
                onClick={() => setIsFullScreen(false)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            <div
              className="h-full flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              {isImage && (
                <img
                  src={mediaUrl}
                  alt=""
                  onDragStart={(e) => e.preventDefault()}

                  className="max-w-full max-h-full object-contain"
                />
              )}
              {isVideo && (
                <video
                  src={mediaUrl}
                  className="max-w-full max-h-full"
                  controls
                  autoPlay
                />
              )}
              {isPdf && !pdfError && (
                <PDFViewer url={mediaUrl} isFullScreen onError={setPdfError} />
              )}
              {isPdf && pdfError && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg">
                  {pdfError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

MediaViewer.propTypes = {
  mediaUrl: PropTypes.any,
  fileName: PropTypes.string,
};

export default MediaViewer;
